import { useState, useCallback, useMemo, useEffect, useRef } from "react";

const DEFAULT = { status: "pending" };

const allAllowedTypes = (allowed) => (allowed?.length ? [...allowed] : []);

const isAllTypesSelected = (selected, allowed) =>
  allowed.length > 0 &&
  selected.length === allowed.length &&
  allowed.every((t) => selected.includes(t));

const toQueueType = (selected, allowed) => {
  if (!allowed.length) return "all";
  if (!selected || selected.length === 0) return "all";
  if (isAllTypesSelected(selected, allowed)) return "all";
  return selected.join(",");
};

const syncSelection = (prevSelected, nextAllowed, prevAllowed) => {
  const nextAll = allAllowedTypes(nextAllowed);
  if (!nextAll.length) return [];
  const valid = prevSelected.filter((t) => nextAll.includes(t));
  // Default: nothing selected → select all
  if (valid.length === 0) return nextAll;
  // Allowed grew and user previously had every old type selected → include new types
  const grew = nextAll.some((t) => !(prevAllowed || []).includes(t));
  if (grew) {
    const oldAllowed = (prevAllowed || []).filter((t) => nextAll.includes(t));
    if (
      oldAllowed.length === 0 ||
      isAllTypesSelected(
        prevSelected.filter((t) => oldAllowed.includes(t)),
        oldAllowed,
      )
    ) {
      return nextAll;
    }
  }
  return valid;
};

export function useApprovalFilters(allowedEntityTypes = []) {
  const allowedKey = allowedEntityTypes.join(",");
  const initialTypes = allAllowedTypes(allowedEntityTypes);
  const [entityTypes, setEntityTypes] = useState(initialTypes);
  const [status, setStatus] = useState(DEFAULT.status);
  const [applied, setApplied] = useState({
    entityTypes: initialTypes,
    status: DEFAULT.status,
  });
  const prevAllowedRef = useRef(allowedEntityTypes);

  // Keep Tür selection in sync: default = all allowed types selected.
  useEffect(() => {
    const prevAllowed = prevAllowedRef.current || [];
    prevAllowedRef.current = allowedEntityTypes;

    setEntityTypes((prev) => {
      const nextTypes = syncSelection(prev, allowedEntityTypes, prevAllowed);
      if (prev.join(",") === nextTypes.join(",")) return prev;
      return nextTypes;
    });
    setApplied((prev) => {
      const nextTypes = syncSelection(
        prev.entityTypes,
        allowedEntityTypes,
        prevAllowed,
      );
      if (prev.entityTypes.join(",") === nextTypes.join(",")) return prev;
      return { ...prev, entityTypes: nextTypes };
    });
  }, [allowedKey, allowedEntityTypes]);

  const reset = useCallback(() => {
    const nextAll = allAllowedTypes(allowedEntityTypes);
    setEntityTypes(nextAll);
    setStatus(DEFAULT.status);
    setApplied({ entityTypes: nextAll, status: DEFAULT.status });
  }, [allowedEntityTypes]);

  const apply = useCallback(() => {
    const types =
      entityTypes.length > 0 ? entityTypes : allAllowedTypes(allowedEntityTypes);
    setEntityTypes(types);
    setApplied({ entityTypes: types, status });
  }, [entityTypes, status, allowedEntityTypes]);

  const listMode = useMemo(() => {
    if (applied.status === "pending") return "pending";
    if (applied.status === "all") return "all";
    return "history";
  }, [applied.status]);

  const queueParams = useMemo(
    () => ({
      pageSize: 100,
      ...(listMode === "history" ? { status: applied.status } : {}),
    }),
    [listMode, applied.status],
  );

  const queueType = useMemo(
    () => toQueueType(applied.entityTypes, allowedEntityTypes),
    [applied.entityTypes, allowedEntityTypes],
  );

  return {
    filters: { entityTypes, setEntityTypes, status, setStatus, reset },
    applied,
    apply,
    listMode,
    queueType,
    queueParams,
    allTypesSelected: isAllTypesSelected(entityTypes, allowedEntityTypes),
  };
}
